/**
 * Lane B (#580): public-safe adaptive sample over the curated DIAGNOSTIC bank.
 * Backend owns live IRT for auth sessions; anon six-turn uses this metered subset.
 * @module features/publicDiagnostic/adaptiveEngine
 */
import { DIAGNOSTIC, type DiagnosticQuestion } from '@/data/diagnosticBank';
import type { AdaptiveQuestionResult } from './types';

export type AdaptationReason = 'harder' | 'easier' | 'same';

export interface AdaptiveTurnQuestion {
  questionId: string;
  category: string;
  stem: string;
  options: { choiceId: string; label: string; text: string }[];
  correctChoiceId: string;
  difficulty: number;
  /** Pam's read after an answer: teaches the pattern, never says right or wrong. */
  pam?: string;
}

/** Fixed difficulties on the public-safe six — mid start, room to step up/down. */
const DIFFICULTY_BY_ID: Record<number, number> = {
  7: 3,
  2: 4,
  10: 2,
  13: 3,
  6: 4,
  17: 5,
};

export const TIMED_QUESTION_SECONDS = 60;

export function toAdaptiveTurnQuestion(q: DiagnosticQuestion): AdaptiveTurnQuestion {
  return {
    questionId: String(q.id),
    category: q.cat,
    stem: q.stem,
    options: q.options.map((o) => ({ choiceId: o.l, label: o.l, text: o.text })),
    correctChoiceId: q.correct,
    difficulty: DIFFICULTY_BY_ID[q.id] ?? 3,
    pam: q.pam,
  };
}

function bank(excludeIds: Iterable<string> = []): AdaptiveTurnQuestion[] {
  const skip = new Set(excludeIds);
  return DIAGNOSTIC.map(toAdaptiveTurnQuestion).filter((q) => !skip.has(q.questionId));
}

/** Turn 1: mid-difficulty item from the public-safe sample. */
export function pickTurn1Question(): AdaptiveTurnQuestion {
  const all = bank();
  return all.find((q) => q.difficulty === 3) ?? all[0];
}

/**
 * Turn 2 reason is derived from turn 1 outcome + the difficulty we actually show.
 * Correct → harder when available; miss → easier when available; else same.
 */
export function deriveAdaptationReason(
  turn1Difficulty: number,
  followupDifficulty: number
): AdaptationReason {
  if (followupDifficulty > turn1Difficulty) return 'harder';
  if (followupDifficulty < turn1Difficulty) return 'easier';
  return 'same';
}

export function pickFollowupQuestion(turn1: Pick<AdaptiveQuestionResult, 'questionId' | 'correct' | 'difficultyShown'>): {
  question: AdaptiveTurnQuestion;
  adaptationReason: AdaptationReason;
} {
  const baseDiff = turn1.difficultyShown ?? 3;
  const rest = bank([turn1.questionId]);

  let candidate: AdaptiveTurnQuestion | undefined;
  if (turn1.correct) {
    candidate = rest
      .filter((q) => q.difficulty > baseDiff)
      .sort((a, b) => a.difficulty - b.difficulty)[0];
  } else {
    candidate = rest
      .filter((q) => q.difficulty < baseDiff)
      .sort((a, b) => b.difficulty - a.difficulty)[0];
  }
  if (!candidate) {
    candidate = rest.find((q) => q.difficulty === baseDiff) ?? rest[0];
  }

  return {
    question: candidate,
    adaptationReason: deriveAdaptationReason(baseDiff, candidate.difficulty),
  };
}

/** Turn 4: next unused sample item (caller supplies prior questionIds). */
export function pickTimedQuestion(excludeIds: string[]): AdaptiveTurnQuestion {
  const rest = bank(excludeIds);
  return rest[0] ?? bank()[0];
}

/** Pam voice — engine demonstrating itself, not a score. */
export function pamAdaptationCopy(reason: AdaptationReason): string {
  switch (reason) {
    case 'harder':
      return "That landed. I'm giving you a harder one next so we can find your edge.";
    case 'easier':
      return "Alright, dialing this one easier so we lock the foundation before we climb.";
    case 'same':
      return "Same level again. I want another look before I move the dial.";
  }
}

export function buildAdaptiveResult(args: {
  turnId: 1 | 2 | 4;
  kind: AdaptiveQuestionResult['kind'];
  question: AdaptiveTurnQuestion;
  choiceId: string;
  adaptationReason?: AdaptationReason;
  timedOut?: boolean;
}): AdaptiveQuestionResult {
  const timedOut = args.timedOut === true;
  const correct = !timedOut && args.choiceId === args.question.correctChoiceId;
  const result: AdaptiveQuestionResult = {
    turnId: args.turnId,
    kind: args.kind,
    completedAt: new Date().toISOString(),
    questionId: args.question.questionId,
    choiceId: timedOut ? '' : args.choiceId,
    correct,
    abilityDelta: timedOut ? 0 : correct ? 1 : -1,
    difficultyShown: args.question.difficulty,
  };
  if (args.adaptationReason) result.adaptationReason = args.adaptationReason;
  if (timedOut) result.timedOut = true;
  return result;
}
